import { IconButton, Spinner, Table } from "@chakra-ui/react";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import GenericTable from "../../../../components/GenericTable";
import GenericTableElement from "../../../../components/GenericTableElement";
import { toaster } from "../../../../components/ui/toaster";
import { Tooltip } from "../../../../components/ui/tooltip";
import { SETTLEMENT_TABLE_COLUMNS } from "../../../../config/tables";
import { Settlement } from "../../../../types/types";
import { useRetrySettlement } from "../hooks/useRetrySettlement";

type Props = {
  isLoading: boolean;
  settlements: Settlement[];
  setShowDetails: (showDetails: {
    type: "invoices" | "adjustments";
    date: string;
    cycle: string;
  }) => void;
};

const SettlementsTable = ({
  isLoading,
  settlements,
  setShowDetails,
}: Props): JSX.Element => {
  const [retryingKeys, setRetryingKeys] = useState<Set<string>>(new Set());

  const { retrySettlement } = useRetrySettlement(
    () => {
      toaster.create({
        title: "Reintentando abono",
        description: "El estado del abono se actualizará en unos momentos.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description: "No se pudo reintentar el abono.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  const handleClickable = (column: string, date: string, cycle: string) => {
    if (column === "amountCollected") {
      setShowDetails({ type: "invoices", date, cycle });
    } else if (column === "adjustments") {
      setShowDetails({ type: "adjustments", date, cycle });
    }
  };

  const handleRetry = (date: string, cycle: string) => {
    const key = `${date}_${cycle}`;
    setRetryingKeys((prev) => new Set(prev).add(key));

    retrySettlement(date, cycle, {
      onSettled: () => {
        setRetryingKeys((prev) => {
          const updated = new Set(prev);
          updated.delete(key);
          return updated;
        });
      },
    });
  };

  return (
    <GenericTable
      interactive
      isLoading={isLoading}
      columns={SETTLEMENT_TABLE_COLUMNS}
    >
      {settlements.map((item, index) => (
        <Table.Row key={index}>
          {SETTLEMENT_TABLE_COLUMNS.map((column) => {
            const value =
              column.key === "feesPostpaid"
                ? item.feesPostpaid
                  ? "postpaid"
                  : "prepaid"
                : item[column.key as keyof Settlement];

            const isCollectedAmount = column.key === "amountCollected";
            const isAdjustmentsWithValue =
              column.key === "adjustments" &&
              typeof value === "number" &&
              value > 0;

            const isClickableAmount =
              isCollectedAmount || isAdjustmentsWithValue;

            const settlementKey = `${item.date}_${item.cycle}`;
            const isRetrying = retryingKeys.has(settlementKey);

            return (
              <GenericTableElement
                key={`${index}-${column.key}`}
                value={value}
                badge={"format" in column && column.format === "badge"}
                formatNumber={
                  "format" in column && column.format === "currency"
                }
                tooltip={
                  "tooltip" in column ? column.tooltip(item) : undefined
                }
                suffix={
                  column.key === "status" && item.status === "error" ? (
                    <Tooltip content="Reintentar">
                      <IconButton
                        aria-label="Reintentar abono"
                        variant="ghost"
                        size="xs"
                        disabled={isRetrying}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRetry(item.date, item.cycle);
                        }}
                      >
                        {isRetrying ? (
                          <Spinner size="sm" />
                        ) : (
                          <RefreshCw size="1rem" />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
                onClick={
                  isClickableAmount
                    ? () => handleClickable(column.key, item.date, item.cycle)
                    : undefined
                }
              />
            );
          })}
        </Table.Row>
      ))}
    </GenericTable>
  );
};

export default SettlementsTable;
